import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FBF6EC] border-2 border-transparent rounded-2xl px-5 py-3.5 text-base font-medium placeholder:text-[#2B0B3C]/35 focus:outline-none focus:border-[#FF007A] focus:ring-4 focus:ring-[#FF007A]/20 transition-all duration-200";

export const Wholesale = () => {
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — we'll be in touch proppa soon.");
      setForm({ business_name: "", contact_name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send that — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="wholesale" className="py-24 lg:py-32 bg-[#4A148C]" data-testid="wholesale-section">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="inline-block bg-[#FFD600] rounded-full px-5 py-2 text-xs font-bold tracking-[0.15em] uppercase text-[#2B0B3C] mb-8">
            Wholesale
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[0.95] text-white">
            Stock the<br /><span className="text-[#FF6B00]">proppa</span> stuff.
          </h2>
          <p className="mt-8 max-w-sm text-base lg:text-lg font-medium leading-relaxed text-white/75">
            Pubs, takeaways, bottle shops and delis — tell us who you are and what you need,
            and we'll come back with trade pricing within one working day.
          </p>
          <div className="mt-12 flex flex-wrap gap-3">
            {["Min order: one mixed case", "UK-wide, 2–3 days", "30-day terms for regulars"].map((chip) => (
              <span key={chip} className="bg-white/10 border-2 border-white/30 rounded-full px-4 py-2 text-xs font-bold text-white">
                {chip}
              </span>
            ))}
          </div>
          <p className="mt-10 text-sm font-medium text-white/60">
            Already a trade customer?{" "}
            <a href="/trade" data-testid="wholesale-trade-link" className="text-[#FFD600] font-bold underline underline-offset-4 hover:text-white transition-colors">
              Sign in for your price list
            </a>
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 bg-white rounded-[28px] shadow-[0_24px_60px_-16px_rgba(0,0,0,0.35)] p-8 lg:p-12"
          data-testid="wholesale-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <input
              required
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="Business name"
              className={inputCls}
              data-testid="wholesale-business-input"
            />
            <input
              required
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Your name"
              className={inputCls}
              data-testid="wholesale-name-input"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email address"
              className={`${inputCls} sm:col-span-2`}
              data-testid="wholesale-email-input"
            />
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="What are you after? (e.g. 10 cases of Proppadoms, 5 cases of Bombay mix…)"
              className={`${inputCls} sm:col-span-2 resize-none`}
              data-testid="wholesale-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="wholesale-submit-button"
            className="group mt-8 inline-flex items-center gap-3 bg-[#FF6B00] text-[#2B0B3C] rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wide shadow-lg hover:bg-[#FFD600] hover:-translate-y-0.5 disabled:opacity-50 transition-[transform,background-color] duration-200"
          >
            {sending ? "Sending…" : "Send enquiry"}
            <ArrowUpRight size={18} strokeWidth={3} className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
